import React from 'react';
import strings from "../../../localization";
import {getError, hasError} from "../../../functions/Validation";
import {Button, TextField, Divider} from "@material-ui/core";
import Grid from '@material-ui/core/Grid';
import DocumentType from "../../../constants/DocumentType";
import ApplicationInternalStatus from '../../../constants/ApplicationInternalStatus';
import {findDataValue} from "../../../util/DropdownUtil";
import UploadSection from '../../../constants/UploadSection';
import CustomFileList from '../../../common/CustomFileList';
import {checkPermission, canViewBankDetails} from "../../../util/SecurityUtil";
import Permission from "../../../constants/Permission";

const BankDetailsForm = ({
                             onChange,
                             errors,
                             data,
                             onDelete,
                             proofOfAccountDetails,
                             onModalOpen,
                             handleToggle,
                             disabled,
                             user,
                             application
                         }) => {

    // Check if user can view bank details
    const canView = canViewBankDetails(user, application);
    
    // Function to generate a random hash string
    const randomHash = (length = 10) => {
        const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    };

    // Function to mask sensitive data
    const maskValue = (value) => {
        if (!canView) {
            return '**********';
        }
        return value || '';
    };

    return (
        <form id='bank-details-form'>
            <Grid container spacing={9} className={'grid-wrapper'}>

                {
                    checkPermission(user.userType, Permission.BANK_DETAILS) &&
                    <React.Fragment>
                        {/* Show message when bank details are masked */}
                        {!canView && (
                            <Grid item md={12}>
                                <div className={'message-wrapper'} style={{backgroundColor: '#ff9800', borderColor: '#ff9800'}}>
                                    <h4>Bank Details Masked</h4>
                                    <div className={'message'}>
                                        Bank details are masked for security. You can see the data structure and length, 
                                        but sensitive information is encrypted. Only the candidate can view their actual banking details.
                                    </div>
                                </div>
                            </Grid>
                        )}
                        
                        <Grid item xl={4} lg={5} md={12}>
                            <div className={'select-container' + (hasError(errors, 'bankName') ? ' error' : '')}>
                                <label>
                                    {strings.bankDetails.bankName} {data.applicationInterlanState}
                                    {!canView && <span style={{color: '#ff9800', marginLeft: '5px'}}>🔒</span>}
                                </label>
                                <TextField
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={hasError(errors, 'bankName')}
                                    helperText={getError(errors, 'bankName')}
                                    fullWidth
                                    name='bankName'
                                    onChange={onChange}
                                    margin="dense"
                                    value={maskValue(data.bankName)}
                                    variant='outlined'
                                    disabled={disabled || !canView}
                                />
                            </div>
                            <div className={'select-container' + (hasError(errors, 'accountHolderName') ? ' error' : '')}>
                                <label>
                                    {strings.bankDetails.accountHolderName}
                                    {!canView && <span style={{color: '#ff9800', marginLeft: '5px'}}>🔒</span>}
                                </label>
                                <TextField
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={hasError(errors, 'accountHolderName')}
                                    helperText={getError(errors, 'accountHolderName')}
                                    fullWidth
                                    name='accountHolderName'
                                    onChange={onChange}
                                    margin="dense"
                                    value={maskValue(data.accountHolderName)}
                                    variant='outlined'
                                    disabled={disabled || !canView}
                                />
                            </div>
                            <div className={'select-container-upload'}>
                                <label>{strings.bankDetails.proofOfAccountDetails}</label>
                                <div className="open-modal-button">
                                    <Button variant="contained" color="primary" disabled={disabled || !canView} onClick={() => {
                                        onModalOpen(UploadSection.Proof_of_Account_Details)
                                    }}>
                                        {strings.bankDetails.uploadFiles}
                                    </Button>
                                    <div className={'tooltip right'}>
                                        <img src={'/images/info.png'}/>
                                        <span className="tooltip-content">
                                    <p>Documents you can upload as proof of account details:</p>
                                    <ul>
                                        <li>Bank Statement</li>
                                        <li>Bank Confirmation Letter</li>
                                    </ul>

                                    <p>Please ensure that the documents are clear and legible. If we are not able to review documents due to the quality of the uploads, this might delay your application.</p>
                                </span>
                                    </div>
                                </div>
                            </div>

                            <Divider style={{marginBottom: '20px'}}/>
                            <CustomFileList onDelete={onDelete} files={proofOfAccountDetails} disable={disabled || !canView}
                                            uploadSection={UploadSection.Proof_of_Account_Details}/>
                        </Grid>

                        <Grid item xl={4} lg={5} md={12}>
                            {
                                findDataValue(data, 'territoryOfWork') === 'UK'
                                    ?
                                    <div>

                                        <div
                                            className={'select-container' + (hasError(errors, 'bankAccountNumber') ? ' error' : '')}>
                                            <label>
                                                {strings.bankDetails.bankAccountNumber}
                                                {!canView && <span style={{color: '#ff9800', marginLeft: '5px'}}>🔒</span>}
                                            </label>
                                            <TextField
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                error={hasError(errors, 'bankAccountNumber')}
                                                helperText={getError(errors, 'bankAccountNumber')}
                                                fullWidth
                                                name='bankAccountNumber'
                                                onChange={onChange}
                                                margin="dense"
                                                value={maskValue(data.bankAccountNumber)}
                                                variant='outlined'
                                                disabled={disabled || !canView}
                                                placeholder="12345678"
                                                inputProps={{
                                                    maxLength: 8,
                                                    pattern: "[0-9]{8}"
                                                }}
                                            />
                                        </div>
                                        <div className={'select-container' + (hasError(errors, 'bankShortCode') ? ' error' : '')}>
                                            <label>
                                                {strings.bankDetails.bankSortCode}
                                                {!canView && <span style={{color: '#ff9800', marginLeft: '5px'}}>🔒</span>}
                                            </label>
                                            <TextField
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                error={hasError(errors, 'bankShortCode')}
                                                helperText={getError(errors, 'bankShortCode')}
                                                fullWidth
                                                name='bankShortCode'
                                                onChange={onChange}
                                                margin="dense"
                                                value={maskValue(data.bankShortCode)}
                                                variant='outlined'
                                                disabled={disabled || !canView}
                                                placeholder="123456"
                                                inputProps={{
                                                    maxLength: 6,
                                                    pattern: "[0-9]{6}"
                                                }}
                                            />
                                        </div>
                                    </div>
                                    :
                                    <div>
                                        <div className={'select-container' + (hasError(errors, 'iban') ? ' error' : '')}>
                                            <label>
                                                {strings.bankDetails.iban}
                                                {!canView && <span style={{color: '#ff9800', marginLeft: '5px'}}>🔒</span>}
                                            </label>
                                            <TextField
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                error={hasError(errors, 'iban')}
                                                helperText={getError(errors, 'iban')}
                                                fullWidth
                                                name='iban'
                                                onChange={onChange}
                                                margin="dense"
                                                value={maskValue(data.iban)}
                                                variant='outlined'
                                                disabled={disabled || !canView}
                                            />
                                        </div>
                                        <div className={'select-container' + (hasError(errors, 'bic') ? ' error' : '')}>
                                            <label>
                                                {strings.bankDetails.bic}
                                                {!canView && <span style={{color: '#ff9800', marginLeft: '5px'}}>🔒</span>}
                                            </label>
                                            <TextField
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                error={hasError(errors, 'bic')}
                                                helperText={getError(errors, 'bic')}
                                                fullWidth
                                                name='bic'
                                                onChange={onChange}
                                                margin="dense"
                                                value={maskValue(data.bic)}
                                                variant='outlined'
                                                disabled={disabled || !canView}
                                            />
                                        </div>
                                    </div>

                            }
                        </Grid>

                        <Grid item md={10}>
                            <div className={'message-wrapper'}>
                                <h4>{strings.bankDetails.bankDetailsConfirmation}</h4>
                                <div className={'message'}>{strings.bankDetails.bankDetailsMessage}</div>
                            </div>

                            <label className="switch" onClick={handleToggle}>
                                <input checked={data.bankDetailConfirmation} disabled={disabled || !canView} type="checkbox" id="togBtn"
                                       name={'bankDetailConfirmation'}/>
                                <div className="slider round">
                                    <span className="on">YES</span>
                                    <span className="off">NO</span>
                                </div>
                            </label>
                            {

                                hasError(errors, 'bankDetailConfirmation') &&
                                <p className={'error'}>
                                    {getError(errors, 'bankDetailConfirmation')}
                                </p>
                            }

                        </Grid>
                    </React.Fragment>
                }

                {
                    !checkPermission(user.userType, Permission.BANK_DETAILS) &&
                    <Grid item md={10}>
                        <div className={'question'}>{ strings.bankDetails.informationRestrictedToHRonly }</div>
                    </Grid>
                }
            </Grid>
            <Divider/>
        </form>
    );
};

export default BankDetailsForm;